import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Briefcase,
  Search,
  MoreHorizontal,
  Trash2,
  Copy,
  ExternalLink,
  Power,
  Loader2,
  CalendarDays,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_TONES = {
  new: "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  contacted: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
  interview: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  waiting: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
  rejected: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
  hired: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};
const STATUS_LABELS = {
  new: "Új jelentkezés",
  contacted: "Kapcsolatfelvétel",
  interview: "Interjú",
  waiting: "Várakozik",
  rejected: "Elutasítva",
  hired: "Felvéve",
};

const emptyForm = { name: "", description: "", location: "", active: true };

export default function RecruitmentPage() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/positions");
      setPositions(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a pozíciókat."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
    // Auto-poll 2 percenként + tab fókuszra azonnal — új jelentkezések
    // gyorsan látszanak a kártyákon (application_counts).
    const interval = setInterval(() => {
      api
        .get("/positions")
        .then(({ data }) => setPositions(data))
        .catch(() => {});
    }, 120000);
    const onFocus = () => {
      api
        .get("/positions")
        .then(({ data }) => setPositions(data))
        .catch(() => {});
    };
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return positions;
    const q = search.trim().toLowerCase();
    return positions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.location && p.location.toLowerCase().includes(q))
    );
  }, [positions, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErr("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      location: p.location || "",
      active: !!p.active,
    });
    setErr("");
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) {
      setErr("A pozíció neve kötelező.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        active: form.active,
      };
      if (editing) {
        const { data } = await api.patch(`/positions/${editing._id}`, payload);
        setPositions((prev) =>
          prev.map((p) => (p._id === editing._id ? { ...p, ...data } : p))
        );
        toast.success("Pozíció frissítve.");
      } else {
        const { data } = await api.post("/positions", payload);
        setPositions((prev) => [data, ...prev]);
        toast.success("Új pozíció létrehozva.");
      }
      setOpen(false);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  const togglePositionActive = async (p) => {
    try {
      const { data } = await api.patch(`/positions/${p._id}`, {
        active: !p.active,
      });
      setPositions((prev) =>
        prev.map((x) => (x._id === p._id ? { ...x, ...data } : x))
      );
      toast.success(
        data.active ? "Pozíció aktiválva." : "Pozíció inaktiválva."
      );
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült frissíteni."
      );
    }
  };

  const onDelete = async (p) => {
    if (
      !window.confirm(
        `Biztosan törlöd a(z) "${p.name}" pozíciót? Az összes jelentkezés is törlődik.`
      )
    )
      return;
    try {
      await api.delete(`/positions/${p._id}`);
      setPositions((prev) => prev.filter((x) => x._id !== p._id));
      toast.success("Pozíció törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  const copyPublicLink = (p) => {
    const url = `${window.location.origin}/apply/${p.slug}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Jelentkezési link a vágólapra másolva."))
      .catch(() => toast.error("Nem sikerült másolni."));
  };

  return (
    <div data-testid="recruitment-page" className="space-y-7">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <Briefcase className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
              Toborzás
            </span>
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tight">
            Nyitott pozíciók
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Hozz létre új pozíciókat, oszd meg a jelentkezési linket, és kezeld a
            beérkező jelölteket státusz szerint.
          </p>
        </div>
        <Button data-testid="new-position-button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Új pozíció
        </Button>
      </header>

      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="position-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés pozícióra vagy helyszínre…"
            className="pl-9"
          />
        </div>
        <Link
          to="/recruitment/interviews"
          data-testid="link-interviews-summary"
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-[hsl(var(--sipx-elevated))] px-3 text-[12.5px] font-medium text-foreground transition-colors hover:bg-muted/60"
        >
          <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
          Interjú összesítő
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Pozíciók betöltése…
        </div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="positions-empty-state"
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center"
        >
          <div className="grid h-12 w-12 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
            <Briefcase className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <p className="text-base font-medium">Még nincs pozíció</p>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            Hozd létre az első pozíciót — a publikus jelentkezési linket
            megoszthatod a közösségi felületeken.
          </p>
          <Button className="mt-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Új pozíció
          </Button>
        </div>
      ) : (
        <div
          data-testid="positions-grid"
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          {filtered.map((p) => (
            <PositionCard
              key={p._id}
              position={p}
              onOpen={() => navigate(`/recruitment/positions/${p._id}`)}
              onEdit={() => openEdit(p)}
              onDelete={() => onDelete(p)}
              onCopyLink={() => copyPublicLink(p)}
              onToggleActive={() => togglePositionActive(p)}
            />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="position-form-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Pozíció szerkesztése" : "Új pozíció"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Frissítsd a pozíció adatait."
                : "Egy egyedi jelentkezési link is létrejön a pozícióhoz."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="space-y-4">
            <FieldLabel label="Pozíció neve *" testId="position-name">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Pl. Pultos, Felszolgáló, Konyhás…"
                maxLength={120}
              />
            </FieldLabel>

            <FieldLabel label="Helyszín" testId="position-location">
              <Input
                value={form.location}
                onChange={(e) =>
                  setForm((f) => ({ ...f, location: e.target.value }))
                }
                placeholder="Pl. Budapest VII. kerület"
                maxLength={200}
              />
            </FieldLabel>

            <FieldLabel label="Leírás" testId="position-description">
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={4}
                placeholder="Pozíció rövid leírása, elvárások, juttatások…"
              />
            </FieldLabel>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-sm font-medium">Aktív (publikus)</p>
                <p className="text-xs text-muted-foreground">
                  Ha inaktív, a jelentkezési link nem érhető el.
                </p>
              </div>
              <Switch
                data-testid="position-active-toggle"
                checked={form.active}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, active: !!v }))
                }
              />
            </div>

            {err && (
              <div
                data-testid="position-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {err}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Mégse
              </Button>
              <Button type="submit" disabled={saving} data-testid="position-submit">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                  </>
                ) : editing ? (
                  "Mentés"
                ) : (
                  "Létrehozás"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PositionCard({
  position,
  onOpen,
  onEdit,
  onDelete,
  onCopyLink,
  onToggleActive,
}) {
  const total = position.application_total || 0;
  const newCount = position.application_counts?.new || 0;
  const interviewCount = position.application_counts?.interview || 0;
  const hiredCount = position.application_counts?.hired || 0;

  return (
    <div
      data-testid={`position-card-${position._id}`}
      className="group flex flex-col rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-5 transition-colors hover:border-foreground/20"
    >
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          data-testid={`position-open-${position._id}`}
        >
          <div className="flex items-center gap-2">
            <h3 className="truncate text-base font-semibold tracking-tight">
              {position.name}
            </h3>
            {!position.active && (
              <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                inaktív
              </span>
            )}
          </div>
          {position.location && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {position.location}
            </p>
          )}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`position-actions-${position._id}`}
              aria-label="Pozíció műveletek"
              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onCopyLink}>
              <Copy className="h-4 w-4" />
              Jelentkezési link másolása
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => window.open(`/apply/${position.slug}`, "_blank")}
            >
              <ExternalLink className="h-4 w-4" />
              Űrlap megnyitása
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>Szerkesztés</DropdownMenuItem>
            <DropdownMenuItem onClick={onToggleActive}>
              <Power className="h-4 w-4" />
              {position.active ? "Inaktiválás" : "Aktiválás"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Törlés
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {position.description && (
        <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {position.description}
        </p>
      )}

      <div className="mt-4 flex items-center gap-2 text-[11px] flex-wrap">
        <StatChip label="Összes" value={total} />
        <StatChip label="Új" value={newCount} tone="new" />
        <StatChip label="Interjú" value={interviewCount} tone="interview" />
        <StatChip label="Felvéve" value={hiredCount} tone="hired" />
      </div>

      <button
        onClick={onOpen}
        data-testid={`position-applications-${position._id}`}
        className="mt-4 inline-flex items-center justify-center rounded-md border border-border px-3 py-2 text-xs font-medium hover:bg-foreground/[0.04]"
      >
        Jelentkezések megtekintése
      </button>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
        tone ? STATUS_TONES[tone] : "bg-foreground/[0.05] text-foreground"
      )}
    >
      <span className="font-semibold">{value}</span>
      <span className="opacity-75">{label}</span>
    </span>
  );
}

function FieldLabel({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

export { STATUS_TONES, STATUS_LABELS };
